// Considered: TreeMapCard — rejected: it is coupled to the tree-map's own sphere shape
// (sphere.properties.*, drag-drop, expand/select, schemas/sphereLookup for perspective
// labels) while scorecard rows are flat SphereData from executeContext (id/label/status/
// points). Wrapping it would mean synthesizing fake tree-map props rather than reusing a
// thin surface. See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { SphereData } from '../../../hooks/useContextData';
import type { ScorecardViewConfig } from 'types/contextView';
import type { StatusValue } from 'types/spherePoints';
import type { GravityBucketKey } from 'types/sphereMetrics';
import SharedStatusBadgeAtom from '../../atoms/status-badge/StatusBadgeAtom';
import ToneBadgeAtom, { type StatusBadgeTone } from '../../atoms/StatusBadge/StatusBadgeAtom';
import VelocityIndicator from '../../atoms/VelocityIndicator/VelocityIndicator';
import './ScorecardCardsMolecule.scss';

const GRAVITY_TONE: Record<GravityBucketKey, StatusBadgeTone> = {
  critical: 'danger',
  high: 'warn',
  medium: 'info',
  low: 'neutral',
  minimal: 'neutral',
};

export interface ScorecardCardsMoleculeProps {
  spheres: SphereData[];
  surfaceColumns: ScorecardViewConfig['surfaceColumns'];
  loading?: boolean;
  error?: string | null;
  className?: string;
}

const ScorecardCardsMolecule: React.FC<ScorecardCardsMoleculeProps> = ({
  spheres,
  surfaceColumns,
  loading = false,
  error = null,
  className = '',
}) => {
  const { t } = useTranslation();

  if (error) {
    return (
      <div className={`scorecard-cards scorecard-cards--error ${className}`.trim()} data-testid="scorecard-cards-error">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`scorecard-cards ${className}`.trim()} data-testid="scorecard-cards-loading">
        {['a', 'b', 'c'].map((key) => (
          <div key={key} className="scorecard-cards__skeleton-card" />
        ))}
      </div>
    );
  }

  if (spheres.length === 0) {
    return (
      <div className={`scorecard-cards scorecard-cards--empty ${className}`.trim()} data-testid="scorecard-cards">
        {t('scorecard.table.empty')}
      </div>
    );
  }

  return (
    <div className={`scorecard-cards ${className}`.trim()} data-testid="scorecard-cards">
      {spheres.map((row) => {
        const status = row.status?.currentValue?.status ?? 'unknown';
        const velocity = row.points?.velocity;
        const gravity = row.points?.gravity;

        return (
          <div key={row.id} className="scorecard-cards__card" data-testid={`scorecard-card-${row.id}`}>
            <div className="scorecard-cards__card-name">
              {String(row.label ?? row.name ?? row.id ?? '')}
            </div>
            <div className="scorecard-cards__card-badges">
              {surfaceColumns.showStatus && (
                <SharedStatusBadgeAtom
                  status={(status as StatusValue) ?? 'unknown'}
                  variant="chip"
                  data-testid={`scorecard-card-status-${row.id}`}
                />
              )}
              {surfaceColumns.showGravity && (
                gravity ? (
                  <ToneBadgeAtom
                    tone={GRAVITY_TONE[gravity.bucket] ?? 'neutral'}
                    label={String(Math.round(gravity.score))}
                    tooltip={t(`scorecard.table.gravityBuckets.${gravity.bucket}`)}
                    data-testid={`scorecard-card-gravity-${row.id}`}
                  />
                ) : (
                  <span className="scorecard-cards__empty-cell">{t('scorecard.table.noData')}</span>
                )
              )}
            </div>
            {surfaceColumns.showVelocity && (
              <div className="scorecard-cards__card-velocity">
                {velocity ? (
                  <VelocityIndicator score={velocity.score} bucket={velocity.bucket} showScore />
                ) : (
                  <span className="scorecard-cards__empty-cell">{t('scorecard.table.noData')}</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ScorecardCardsMolecule;
